import React from "react";
import {
  Stethoscope, Home, Building2, UtensilsCrossed, ShoppingBag, Car, Wrench, Dumbbell,
  Briefcase, Film, ShoppingCart, LineChart, Calculator, Landmark, Sparkles, Check, Gem, Scissors,
  FlaskConical,
} from "lucide-react";

const ICONS = {
  Stethoscope, Home, Building2, UtensilsCrossed, ShoppingBag, Car, Wrench, Dumbbell,
  Briefcase, Film, ShoppingCart, LineChart, Calculator, Landmark, Sparkles, Gem, Scissors, FlaskConical,
};

const pill = (on) => ({
  border: "1px solid " + (on ? "var(--blue)" : "var(--border)"),
  background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)",
  borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer",
});

// Merge base (sell-type) + industry questions, dedup by key.
export function collectQuestions(schema, sellType, industry, includeBase = true) {
  if (!schema) return [];
  const base = includeBase ? (schema.sell_type_questions?.[sellType] || []) : [];
  const ind = schema.industry_questions?.[industry] || [];
  const seen = new Set();
  const out = [];
  [...base, ...ind].forEach((q) => { if (!seen.has(q.key)) { seen.add(q.key); out.push(q); } });
  return out;
}

export function missingRequired(schema, sellType, industry, intake, includeBase = true) {
  return collectQuestions(schema, sellType, industry, includeBase)
    .filter((q) => q.required)
    .filter((q) => {
      const v = (intake || {})[q.key];
      if (q.type === "bool") return v !== true;
      if (Array.isArray(v)) return v.length === 0;
      return v === undefined || v === null || String(v).trim() === "";
    })
    .map((q) => q.label);
}

export function PlaybookCard({ playbook, title = "Recommended starter plan" }) {
  if (!playbook) return null;
  const chans = playbook.channels || [];
  return (
    <div className="sr-card" style={{ background: "#F7FAFF", border: "1px solid #DCE6F5" }} data-testid="playbook-card">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Sparkles size={15} style={{ color: "var(--blue)" }} />
        <strong style={{ color: "var(--navy)", fontSize: 14 }}>{title}: {playbook.name}</strong>
      </div>
      <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Goal: <b style={{ color: "var(--navy)" }}>{playbook.goal}</b> · Suggested media ~${Number(playbook.suggested_budget || 0).toLocaleString()}/mo <span style={{ opacity: 0.7 }}>(starting point — you set the budget)</span>
      </div>
      {playbook.offer && <div style={{ fontSize: 13, color: "var(--navy)", marginBottom: 8 }}>💡 Offer idea: {playbook.offer}</div>}
      {chans.length > 0 && (
        <div style={{ marginBottom: 8 }} data-testid="playbook-channels">
          <div className="sr-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Suggested channel mix</div>
          <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 10 }}>
            {chans.map((c, i) => (
              <div key={c.channel} style={{ width: `${c.pct}%`, background: ["#2F6BFF", "#E0A94A", "#4CAF82", "#9B6BDF", "#E06A6A"][i % 5] }} title={`${c.channel} ${c.pct}%`} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            {chans.map((c) => <span key={c.channel} className="sr-muted" style={{ fontSize: 11 }}>{c.channel} {c.pct}%</span>)}
          </div>
        </div>
      )}
      {(playbook.creative_ideas || []).length > 0 && (
        <div style={{ fontSize: 12, color: "var(--navy)" }}>
          <span className="sr-muted" style={{ fontSize: 11 }}>Creative ideas: </span>{(playbook.creative_ideas || []).join(" · ")}
        </div>
      )}
    </div>
  );
}

function Field({ q, value, onChange, idPrefix }) {
  const tid = `${idPrefix}-q-${q.key}`;
  const set = (v) => onChange(q.key, v);
  if (q.type === "textarea") {
    return (
      <div className="sr-field" style={{ gridColumn: "1 / -1" }}>
        <label className="sr-label">{q.label}{q.required ? " *" : ""}</label>
        <textarea className="sr-input" rows={3} value={value || ""} placeholder={q.placeholder || ""}
          onChange={(e) => set(e.target.value)} data-testid={tid} />
      </div>
    );
  }
  if (q.type === "select") {
    return (
      <div className="sr-field">
        <label className="sr-label">{q.label}{q.required ? " *" : ""}</label>
        <select className="sr-select" value={value || ""} onChange={(e) => set(e.target.value)} data-testid={tid}>
          <option value="">Select…</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (q.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="sr-field" style={{ gridColumn: "1 / -1" }}>
        <label className="sr-label">{q.label}{q.required ? " *" : ""}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid={tid}>
          {(q.options || []).map((o) => {
            const on = arr.includes(o);
            return (
              <button key={o} type="button" onClick={() => toggle(o)} style={pill(on)}
                data-testid={`${tid}-${o.replace(/\s+/g, "-").toLowerCase()}`}>{on ? "✓ " : ""}{o}</button>
            );
          })}
        </div>
      </div>
    );
  }
  if (q.type === "bool") {
    const on = value === true;
    return (
      <div className="sr-field" style={{ gridColumn: "1 / -1" }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "var(--navy)", cursor: "pointer" }} data-testid={`${tid}-label`}>
          <input type="checkbox" checked={on} onChange={(e) => set(e.target.checked)} data-testid={tid} style={{ marginTop: 3 }} />
          <span>{q.label}{q.required ? " *" : ""}</span>
        </label>
      </div>
    );
  }
  // text | url | number | currency
  const isNum = q.type === "number" || q.type === "currency";
  return (
    <div className="sr-field">
      <label className="sr-label">{q.label}{q.required ? " *" : ""}</label>
      <div style={{ position: "relative" }}>
        {q.type === "currency" && <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)", fontSize: 13 }}>$</span>}
        <input className="sr-input" type={isNum ? "number" : "text"} value={value ?? ""} placeholder={q.placeholder || ""}
          onChange={(e) => set(isNum ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          style={q.type === "currency" ? { paddingLeft: 22 } : {}} data-testid={tid} />
      </div>
      {q.help && <p className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>{q.help}</p>}
    </div>
  );
}

/**
 * Shared sell-type + industry + intake capture. `sections` picks which blocks render.
 * Used by the admin "add client" wizard and the client self-serve onboarding.
 */
export default function IndustryIntake({
  schema, sellType, onSellType, industry, onIndustry, googleListed, onGoogleListed,
  intake = {}, onIntake, sections = ["sellType", "industry", "google", "questions"],
  includeBaseQuestions = true, idPrefix = "intake",
}) {
  if (!schema) return null;
  const show = (s) => sections.includes(s);
  const setIntake = (key, val) => onIntake({ ...(intake || {}), [key]: val });
  const questions = collectQuestions(schema, sellType, industry, includeBaseQuestions);

  return (
    <div data-testid={`${idPrefix}-root`}>
      {show("sellType") && (
        <div className="sr-field" data-testid={`${idPrefix}-selltype`}>
          <label className="sr-label">How do you sell to customers?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginTop: 6 }}>
            {(schema.sell_types || []).map((o) => {
              const on = sellType === o.key;
              return (
                <button key={o.key} type="button" onClick={() => onSellType(o.key)} data-testid={`${idPrefix}-selltype-${o.key}`}
                  style={{ textAlign: "left", border: "2px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "#EAF2FF" : "#fff", borderRadius: 12, padding: "12px 14px", cursor: "pointer" }}>
                  <strong style={{ color: "var(--navy)", fontSize: 14 }}>{o.label}</strong>
                  <div className="sr-muted" style={{ fontSize: 12 }}>{o.hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {show("industry") && (
        <div className="sr-field" data-testid={`${idPrefix}-industry`} style={{ marginTop: show("sellType") ? 16 : 0 }}>
          <label className="sr-label">What industry is this business?</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10, marginTop: 6 }}>
            {(schema.industries || []).map((it) => {
              const on = industry === it.slug;
              const Ico = ICONS[it.icon] || Sparkles;
              return (
                <button key={it.slug} type="button" onClick={() => onIndustry(it.slug)} data-testid={`${idPrefix}-industry-${it.slug}`}
                  style={{ textAlign: "left", border: "2px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "#EAF2FF" : "#fff", borderRadius: 12, padding: "12px 14px", cursor: "pointer", display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ color: on ? "var(--blue)" : "var(--navy)" }}><Ico size={20} /></span>
                  <strong style={{ color: "var(--navy)", fontSize: 14 }}>{it.label}</strong>
                  {on && <Check size={15} style={{ color: "var(--blue)", marginLeft: "auto" }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {show("google") && sellType !== "online" && (
        <div className="sr-field" data-testid={`${idPrefix}-google`} style={{ marginTop: 16 }}>
          <label className="sr-label">Is this business listed on Google Maps?</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[{ v: true, l: "Yes — find it on Google" }, { v: false, l: "No / skip" }].map((o) => (
              <button key={String(o.v)} type="button" onClick={() => onGoogleListed(o.v)} style={pill(googleListed === o.v)}
                data-testid={`${idPrefix}-google-${o.v ? "yes" : "no"}`}>{o.l}</button>
            ))}
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>If it's not on Google Maps, we'll skip the search and you can enter details manually.</p>
        </div>
      )}

      {show("questions") && questions.length > 0 && (
        <div data-testid={`${idPrefix}-questions`} style={{ marginTop: 16 }}>
          <label className="sr-label" style={{ marginBottom: 6 }}>A few details so the AI builds the right ads</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {questions.map((q) => (
              <Field key={q.key} q={q} value={(intake || {})[q.key]} onChange={setIntake} idPrefix={idPrefix} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
